import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { Download, PictureAsPdf, Refresh, Storefront } from '@mui/icons-material';
import axios from 'axios';
import { API_URL } from './constants';
import SnusProductDetailPopup from './snus/SnusProductDetailPopup';
import PublishReportControls from './snus/PublishReportControls';
import RawDataFilterBar from './snus/RawDataFilterBar';

const RAW_DATASETS = [
  { id: 'dashboard', label: 'Prices' },
  { id: 'product_statuses', label: 'Availability' },
  { id: 'promos', label: 'Visibility' },
];
const RAW_PAGE_SIZES = [50, 100, 250];

function weekCsvLabel(week) {
  if (!week?.csv_on_disk) return null;
  const bits = [];
  if (week.csv_prices) bits.push(`P${week.csv_prices}`);
  if (week.csv_availability) bits.push(`A${week.csv_availability}`);
  if (week.csv_visibility) bits.push(`V${week.csv_visibility}`);
  return bits.length ? `CSV ${bits.join(' ')}` : 'CSV';
}

const PERIODS = [
  { id: 'weekly', label: 'Weekly' },
  { id: 'daily', label: 'Daily' },
  { id: 'monthly', label: 'Monthly' },
  { id: 'custom', label: 'Custom' },
];
const MAX_DOWNLOAD_DAYS = 92;
const MAX_DOWNLOAD_RETAILERS = 5;

function isoDate(dt) {
  const month = String(dt.getMonth() + 1).padStart(2, '0');
  const day = String(dt.getDate()).padStart(2, '0');
  return `${dt.getFullYear()}-${month}-${day}`;
}

function formatCount(value) {
  return Number(value || 0).toLocaleString();
}

function downloadPortion(progress, now, refreshing) {
  const total = Number(progress?.total) || 0;
  const done = Number(progress?.done) || 0;
  if (!total) return { bar: 0, percent: 0 };
  if (!refreshing || done >= total) {
    const ratio = Math.min(1, done / total);
    return { bar: ratio * 100, percent: done >= total ? 100 : Math.floor(ratio * 100) };
  }
  let units = done;
  if (progress.dayStartedAt && progress.avgDayMs > 0) {
    const elapsed = Math.max(0, now - progress.dayStartedAt);
    units += Math.min(0.99, elapsed / progress.avgDayMs);
  }
  const capUnits = done + 0.99;
  const ratio = Math.min(units, capUnits) / total;
  const nextShare = Math.floor(((done + 1) / total) * 100) - 1;
  const percent = Math.max(Math.floor((done / total) * 100), Math.min(Math.floor(ratio * 100), nextShare));
  return { bar: ratio * 100, percent };
}

function formatEta(ms) {
  const seconds = Math.max(0, Math.floor(Number(ms) / 1000));
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  const rem = seconds % 60;
  if (minutes < 60) return rem ? `${minutes}m ${rem}s` : `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  const minRem = minutes % 60;
  return minRem ? `${hours}h ${minRem}m` : `${hours}h`;
}

function eachDate(start, end) {
  if (!start || !end || end < start) return [];
  const out = [];
  const cur = new Date(`${start}T00:00:00`);
  const last = new Date(`${end}T00:00:00`);
  if (Number.isNaN(cur.getTime()) || Number.isNaN(last.getTime())) return [];
  while (cur <= last && out.length < 400) {
    out.push(isoDate(cur));
    cur.setDate(cur.getDate() + 1);
  }
  return out;
}

function todayParts() {
  const now = new Date();
  return {
    day: isoDate(now),
    month: `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`,
  };
}

function isoWeekBounds(weekKey) {
  const match = /^(\d{4})-W(\d{2})$/.exec(weekKey || '');
  if (!match) return null;
  const year = Number(match[1]);
  const week = Number(match[2]);
  if (week < 1 || week > 53) return null;
  const jan4 = new Date(year, 0, 4);
  const week1Monday = new Date(jan4);
  week1Monday.setDate(jan4.getDate() - ((jan4.getDay() + 6) % 7));
  const monday = new Date(week1Monday);
  monday.setDate(week1Monday.getDate() + (week - 1) * 7);
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  return { start: isoDate(monday), end: isoDate(sunday) };
}

function monthBounds(month) {
  const match = /^(\d{4})-(\d{2})$/.exec(month || '');
  if (!match) return null;
  const year = Number(match[1]);
  const mon = Number(match[2]);
  if (mon < 1 || mon > 12) return null;
  const last = new Date(year, mon, 0).getDate();
  const mm = String(mon).padStart(2, '0');
  return { start: `${year}-${mm}-01`, end: `${year}-${mm}-${String(last).padStart(2, '0')}` };
}

function resolvePeriodRange(period, { week, day, month, start, end, weeks }) {
  if (period === 'weekly') {
    const known = (weeks || []).find((item) => item.week_key === week);
    if (known?.week_start && known?.week_end) {
      return { start: known.week_start, end: known.week_end, label: known.week_label || week };
    }
    const bounds = isoWeekBounds(week);
    return bounds ? { ...bounds, label: week } : null;
  }
  if (period === 'daily') {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(day || '')) return null;
    return { start: day, end: day, label: day };
  }
  if (period === 'monthly') {
    const bounds = monthBounds(month);
    return bounds ? { ...bounds, label: month } : null;
  }
  if (period === 'custom') {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(start || '') || !/^\d{4}-\d{2}-\d{2}$/.test(end || '')) return null;
    if (end < start) return null;
    return { start, end, label: `${start} – ${end}` };
  }
  return null;
}

function PeriodFields({
  period,
  onPeriod,
  weekControl,
  day,
  onDay,
  month,
  onMonth,
  start,
  onStart,
  end,
  onEnd,
}) {
  return (
    <>
      <FormControl size="small" sx={{ minWidth: 140 }}>
        <InputLabel>Period</InputLabel>
        <Select label="Period" value={period} onChange={(e) => onPeriod(e.target.value)}>
          {PERIODS.map((item) => (
            <MenuItem key={item.id} value={item.id}>
              {item.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      {period === 'weekly' ? weekControl : null}
      {period === 'daily' ? (
        <TextField
          size="small"
          type="date"
          label="Day"
          value={day}
          onChange={(e) => onDay(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
      ) : null}
      {period === 'monthly' ? (
        <TextField
          size="small"
          type="month"
          label="Month"
          value={month}
          onChange={(e) => onMonth(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
      ) : null}
      {period === 'custom' ? (
        <>
          <TextField
            size="small"
            type="date"
            label="Start"
            value={start}
            onChange={(e) => onStart(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            size="small"
            type="date"
            label="End"
            value={end}
            onChange={(e) => onEnd(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
        </>
      ) : null}
    </>
  );
}
const apiBase = `${API_URL}/sweden-snus-admin/retailer`;

function shelfPdfFilename(parts) {
  const slug = parts
    .filter(Boolean)
    .map((part) => String(part).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, ''))
    .filter(Boolean)
    .join('-');
  return `weekly-shelf-${slug || 'report'}.pdf`;
}

function formatCell(value) {
  if (value === null || value === undefined) return '';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

function formatWow(wow) {
  if (wow == null || Number.isNaN(wow)) return '—';
  const n = Number(wow);
  if (n > 0) return `↑ ${Math.abs(n)} pts WoW`;
  if (n < 0) return `↓ ${Math.abs(n)} pts WoW`;
  return '→ flat WoW';
}

export default function SwedenSnusRetailerTab() {
  const [retailerOptions, setRetailerOptions] = useState([]);
  const [retailerTable, setRetailerTable] = useState('ocado_uk');
  const [meta, setMeta] = useState(null);
  const [table, setTable] = useState('dashboard');
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [brand, setBrand] = useState('');
  const [category, setCategory] = useState('');
  const [weeks, setWeeks] = useState([]);
  const [selectedWeek, setSelectedWeek] = useState('');
  const [reportPeriod, setReportPeriod] = useState('weekly');
  const [reportDay, setReportDay] = useState(() => todayParts().day);
  const [reportMonth, setReportMonth] = useState(() => todayParts().month);
  const [reportStart, setReportStart] = useState('');
  const [reportEnd, setReportEnd] = useState('');
  const [weeksLoading, setWeeksLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [brandsByCategory, setBrandsByCategory] = useState({});
  const [filtersLoading, setFiltersLoading] = useState(false);
  const [health, setHealth] = useState(null);
  const [healthLoading, setHealthLoading] = useState(false);
  const [skuRankings, setSkuRankings] = useState(null);
  const [skuLoading, setSkuLoading] = useState(false);
  const [skuPopupOpen, setSkuPopupOpen] = useState(false);
  const [skuDetail, setSkuDetail] = useState(null);
  const [skuDetailLoading, setSkuDetailLoading] = useState(false);
  const [skuDetailError, setSkuDetailError] = useState(null);
  const [selectedSkuKey, setSelectedSkuKey] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshMsg, setRefreshMsg] = useState(null);
  const [weekDays, setWeekDays] = useState([]);
  const [loadingDay, setLoadingDay] = useState(null);
  const [exportingPdf, setExportingPdf] = useState(false);
  const [detailTab, setDetailTab] = useState('health');
  const [rawPage, setRawPage] = useState(0);
  const [rawPageSize, setRawPageSize] = useState(100);
  const [rawQuery, setRawQuery] = useState('');
  const [rawQueryDebounced, setRawQueryDebounced] = useState('');
  const [rawBrand, setRawBrand] = useState('');
  const [rawCategory, setRawCategory] = useState('');
  const [rawRetailer, setRawRetailer] = useState('');
  const [rawDay, setRawDay] = useState('');
  const reportKey = `${reportPeriod}|${selectedWeek}|${reportDay}|${reportMonth}|${reportStart}|${reportEnd}`;
  const [rawWeekSeen, setRawWeekSeen] = useState(reportKey);
  let rawDayForScope = rawDay;
  if (rawWeekSeen !== reportKey) {
    setRawWeekSeen(reportKey);
    setRawDay('');
    rawDayForScope = '';
  }
  const rawScope = `${retailerTable}|${reportKey}|${table}|${rawQueryDebounced}|${rawBrand}|${rawCategory}|${rawRetailer}|${rawDayForScope}`;
  const [rawScopeSeen, setRawScopeSeen] = useState(rawScope);
  if (rawScopeSeen !== rawScope) {
    setRawScopeSeen(rawScope);
    setRawPage(0);
  }
  const [catalogError, setCatalogError] = useState(null);
  const [catalogCountries, setCatalogCountries] = useState([]);
  const [downloadCountry, setDownloadCountry] = useState('');
  const [downloadRetailers, setDownloadRetailers] = useState([]);
  const [downloadWeeks, setDownloadWeeks] = useState([]);
  const [downloadWeek, setDownloadWeek] = useState('');
  const [downloadWeeksLoading, setDownloadWeeksLoading] = useState(false);
  const [downloadPeriod, setDownloadPeriod] = useState('weekly');
  const [downloadDay, setDownloadDay] = useState(() => todayParts().day);
  const [downloadMonth, setDownloadMonth] = useState(() => todayParts().month);
  const [downloadStart, setDownloadStart] = useState('');
  const [downloadEnd, setDownloadEnd] = useState('');
  const [downloadProgress, setDownloadProgress] = useState(null);
  const [downloadTables, setDownloadTables] = useState({
    dashboard: true,
    product_statuses: true,
    promos: true,
  });
  const downloadStop = useRef(false);
  const downloadAbort = useRef(null);

  const selectedMeta =
    retailerOptions.find((opt) => opt.table === retailerTable) || meta || null;
  const positionsTable = selectedMeta?.positions_table || `${retailerTable}_positions`;
  const retailerLabel = selectedMeta?.label || retailerTable;

  const loadCatalog = useCallback(async (preferTable) => {
    setCatalogError(null);
    try {
      const res = await axios.get(`${apiBase}/tables`, { withCredentials: true });
      const next = res.data?.tables || [];
      setRetailerOptions(next);
      setCatalogCountries(res.data?.countries || []);
      setRetailerTable((prev) => {
        const prepared = next.filter((t) => t.prepared);
        const prefer = preferTable || prev;
        if (prefer && prepared.some((t) => t.table === prefer)) return prefer;
        if (prev && prepared.some((t) => t.table === prev)) return prev;
        return prepared[0]?.table || '';
      });
    } catch (e) {
      setCatalogError(e.response?.data?.detail || e.message || 'Failed to list ecommerce_prod tables');
    }
  }, []);

  const loadMeta = useCallback(async () => {
    if (!retailerTable) return;
    try {
      const res = await axios.get(`${apiBase}/meta`, {
        params: { retailer_table: retailerTable },
        withCredentials: true,
      });
      setMeta(res.data);
    } catch (e) {
      setMeta(null);
    }
  }, [retailerTable]);

  const loadPreview = useCallback(async () => {
    const range = resolvePeriodRange(reportPeriod, {
      week: selectedWeek,
      day: reportDay,
      month: reportMonth,
      start: reportStart,
      end: reportEnd,
      weeks,
    });
    if (!retailerTable || !range) return;
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${apiBase}/datasets/preview`, {
        params: {
          retailer_table: retailerTable,
          table,
          limit: rawPageSize,
          offset: rawPage * rawPageSize,
          ...(reportPeriod === 'weekly' && selectedWeek
            ? { week: selectedWeek }
            : { start_date: range.start, end_date: range.end }),
          ...(rawQueryDebounced ? { q: rawQueryDebounced } : {}),
          ...(rawBrand ? { brand: rawBrand } : {}),
          ...(rawCategory ? { category: rawCategory } : {}),
          ...(rawRetailer ? { retailer: rawRetailer } : {}),
          ...(rawDayForScope ? { day: rawDayForScope } : {}),
        },
        withCredentials: true,
      });
      setPreview(res.data);
    } catch (e) {
      setPreview(null);
      setError(e.response?.data?.detail || e.message || 'Failed to load preview');
    } finally {
      setLoading(false);
    }
  }, [retailerTable, table, selectedWeek, reportPeriod, reportDay, reportMonth, reportStart, reportEnd, weeks, rawPage, rawPageSize, rawQueryDebounced, rawBrand, rawCategory, rawRetailer, rawDayForScope]);

  const loadWeeks = useCallback(async (fresh = false) => {
    if (!retailerTable) return;
    setWeeksLoading(true);
    try {
      const res = await axios.get(`${apiBase}/snapshots`, {
        params: { retailer_table: retailerTable, ...(fresh ? { fresh: true } : {}) },
        withCredentials: true,
      });
      const nextWeeks = res.data?.weeks || [];
      setWeeks(nextWeeks);
      setSelectedWeek((prev) => {
        if (prev && nextWeeks.some((w) => w.week_key === prev)) return prev;
        return nextWeeks[0]?.week_key || '';
      });
    } catch (e) {
      setWeeks([]);
      setError(e.response?.data?.detail || e.message || 'Failed to load weeks');
    } finally {
      setWeeksLoading(false);
    }
  }, [retailerTable]);

  const reportRange = useMemo(
    () =>
      resolvePeriodRange(reportPeriod, {
        week: selectedWeek,
        day: reportDay,
        month: reportMonth,
        start: reportStart,
        end: reportEnd,
        weeks,
      }),
    [reportPeriod, selectedWeek, reportDay, reportMonth, reportStart, reportEnd, weeks]
  );

  const weekFilterReq = useRef(0);
  const loadWeekFilters = useCallback(async () => {
    const req = ++weekFilterReq.current;
    if (!retailerTable || !reportRange) {
      setCategories([]);
      setBrands([]);
      setBrandsByCategory({});
      setCategory('');
      setBrand('');
      return;
    }
    setFiltersLoading(true);
    try {
      const params = { retailer_table: retailerTable };
      if (reportPeriod === 'weekly' && selectedWeek) params.week = selectedWeek;
      else {
        params.start_date = reportRange.start;
        params.end_date = reportRange.end;
      }
      const res = await axios.get(`${apiBase}/week-filters`, {
        params,
        withCredentials: true,
      });
      if (req !== weekFilterReq.current) return;
      const nextCategories = res.data?.categories || [];
      const nextByCat = res.data?.brands_by_category || {};
      setCategories(nextCategories);
      setBrandsByCategory(nextByCat);
      setCategory((prev) => {
        if (prev && nextCategories.includes(prev)) return prev;
        return nextCategories[0] || '';
      });
    } catch (e) {
      if (req !== weekFilterReq.current) return;
      setCategories([]);
      setBrands([]);
      setBrandsByCategory({});
      setCategory('');
      setBrand('');
      setError(e.response?.data?.detail || e.message || 'Failed to load week filters');
    } finally {
      if (req === weekFilterReq.current) setFiltersLoading(false);
    }
  }, [retailerTable, selectedWeek, reportPeriod, reportRange]);

  useEffect(() => {
    const nextBrands = category
      ? brandsByCategory[category] || []
      : Object.values(brandsByCategory)
          .flat()
          .filter((v, i, a) => a.indexOf(v) === i);
    setBrands(nextBrands);
    setBrand((prev) => {
      if (prev && nextBrands.includes(prev)) return prev;
      return nextBrands[0] || '';
    });
  }, [category, brandsByCategory]);

  const loadHealth = useCallback(async () => {
    setHealthLoading(true);
    setSkuLoading(true);
    setError(null);
    const range = resolvePeriodRange(reportPeriod, {
      week: selectedWeek,
      day: reportDay,
      month: reportMonth,
      start: reportStart,
      end: reportEnd,
      weeks,
    });
    if (!range) {
      setHealthLoading(false);
      setSkuLoading(false);
      setError('Choose a period before running intelligence');
      return;
    }
    const params = { retailer_table: retailerTable, brand, category };
    if (reportPeriod === 'weekly' && selectedWeek) params.week = selectedWeek;
    else {
      params.start_date = range.start;
      params.end_date = range.end;
      params.period = reportPeriod;
    }
    try {
      const [oppRes, skuRes] = await Promise.all([
        axios.get(`${apiBase}/brand-opportunity`, { params, withCredentials: true }),
        axios.get(`${apiBase}/sku-rankings`, { params: { ...params, limit: 30 }, withCredentials: true }),
      ]);
      setHealth(oppRes.data);
      setSkuRankings(skuRes.data);
    } catch (e) {
      setHealth(null);
      setSkuRankings(null);
      setError(e.response?.data?.detail || e.message || 'Intelligence lookup failed');
    } finally {
      setHealthLoading(false);
      setSkuLoading(false);
    }
  }, [retailerTable, brand, category, selectedWeek, reportPeriod, reportDay, reportMonth, reportStart, reportEnd, weeks]);

  const openSkuDetail = useCallback(
    async (sku) => {
      if (!sku?.sku_key) return;
      setSelectedSkuKey(sku.sku_key);
      setSkuPopupOpen(true);
      setSkuDetail(null);
      setSkuDetailError(null);
      setSkuDetailLoading(true);
      try {
        const params = {
          retailer_table: retailerTable,
          sku_key: sku.sku_key,
          brand,
          category,
        };
        if (selectedWeek) params.week = selectedWeek;
        const res = await axios.get(`${apiBase}/sku-detail`, {
          params,
          withCredentials: true,
        });
        setSkuDetail(res.data);
      } catch (e) {
        setSkuDetail(null);
        setSkuDetailError(e.response?.data?.detail || e.message || 'Failed to load product detail');
      } finally {
        setSkuDetailLoading(false);
      }
    },
    [retailerTable, brand, category, selectedWeek]
  );

  const loadWeekDays = useCallback(async () => {
    if (!retailerTable || !reportRange) {
      setWeekDays([]);
      return;
    }
    const params = { retailer_table: retailerTable };
    if (reportPeriod === 'weekly' && selectedWeek) params.week = selectedWeek;
    else {
      params.start_date = reportRange.start;
      params.end_date = reportRange.end;
    }
    try {
      const res = await axios.get(`${apiBase}/week-days`, {
        params,
        withCredentials: true,
      });
      setWeekDays(res.data?.days || []);
    } catch (e) {
      setWeekDays([]);
    }
  }, [retailerTable, selectedWeek, reportPeriod, reportRange]);

  const stopDownload = () => {
    downloadStop.current = true;
    downloadAbort.current?.abort();
  };

  const [etaNow, setEtaNow] = useState(() => Date.now());
  useEffect(() => {
    if (!refreshing) return undefined;
    setEtaNow(Date.now());
    const timer = setInterval(() => setEtaNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [refreshing]);
  const downloadEtaLabel = refreshing
    ? downloadProgress?.etaEndsAt
      ? formatEta(downloadProgress.etaEndsAt - etaNow)
      : downloadProgress?.fetchRemaining === 0
        ? null
        : '…'
    : null;

  const refreshDailyCsv = useCallback(
    async (tables) => {
      const range = resolvePeriodRange(downloadPeriod, {
        week: downloadWeek,
        day: downloadDay,
        month: downloadMonth,
        start: downloadStart,
        end: downloadEnd,
        weeks: downloadWeeks,
      });
      const dates = range ? eachDate(range.start, range.end) : [];
      const retailers = downloadRetailers.slice(0, MAX_DOWNLOAD_RETAILERS);
      if (!retailers.length || !dates.length) return;
      if (dates.length > MAX_DOWNLOAD_DAYS) {
        setError(`That range is ${dates.length} days. Narrow it to ${MAX_DOWNLOAD_DAYS} days or fewer.`);
        return;
      }
      downloadStop.current = false;
      setRefreshing(true);
      setRefreshMsg(null);
      setError(null);
      const total = retailers.length * dates.length;
      let done = 0;
      let fetchedDone = 0;
      let fetchTimeMs = 0;
      const shapes = {};
      for (const id of tables) {
        shapes[id] = { rows: 0, columns: 0 };
      }
      const snapshotShapes = () =>
        Object.fromEntries(
          Object.entries(shapes).map(([id, shape]) => [id, { rows: shape.rows, columns: shape.columns }])
        );
      const publishProgress = (day, retailer, retailerIndex, stopped = false) => {
        const remaining = total - done;
        const avgDayMs = fetchedDone > 0 ? fetchTimeMs / fetchedDone : 0;
        const etaMs = fetchedDone > 0 && remaining > 0 ? avgDayMs * remaining : 0;
        const meta = retailerOptions.find((opt) => opt.table === retailer);
        setDownloadProgress({
          done,
          total,
          day,
          retailer,
          retailerLabel: meta?.label || retailer,
          retailerIndex,
          retailerTotal: retailers.length,
          stopped,
          tables,
          shapes: snapshotShapes(),
          etaEndsAt: fetchedDone > 0 && remaining > 0 ? Date.now() + etaMs : null,
          fetchRemaining: remaining,
          dayStartedAt: Date.now(),
          avgDayMs,
        });
      };
      publishProgress(dates[0], retailers[0], 1);
      let failedRetailer = '';
      try {
        for (let retailerIndex = 0; retailerIndex < retailers.length; retailerIndex += 1) {
          if (downloadStop.current) break;
          const retailer = retailers[retailerIndex];
          const meta = retailerOptions.find((opt) => opt.table === retailer);
          const retailerTables =
            meta?.has_positions === false ? tables.filter((id) => id !== 'promos') : tables;
          const alreadySaved = new Set();
          try {
            const statusRes = await axios.get(`${apiBase}/week-days`, {
              params: {
                retailer_table: retailer,
                start_date: dates[0],
                end_date: dates[dates.length - 1],
              },
              withCredentials: true,
            });
            for (const row of statusRes.data?.days || []) {
              const flags = row.datasets || {};
              if (row.date && retailerTables.every((id) => flags[id])) alreadySaved.add(row.date);
            }
          } catch {
            // Days the download itself reports as already saved are still left out of the ETA.
          }
          for (const day of dates) {
            if (downloadStop.current) break;
            publishProgress(day, retailer, retailerIndex + 1);
            const controller = new AbortController();
            downloadAbort.current = controller;
            const requestStarted = Date.now();
            const res = await axios.post(
              `${apiBase}/refresh-data`,
              { date: day, retailer_table: retailer, force: false, tables: retailerTables },
              {
                withCredentials: true,
                headers: { 'content-type': 'application/json' },
                signal: controller.signal,
              }
            );
            const perTable = res.data?.per_table || {};
            for (const id of retailerTables) {
              const piece = perTable[id];
              if (!piece || !shapes[id]) continue;
              shapes[id].rows += Number(piece.rows) || 0;
              shapes[id].columns = Math.max(shapes[id].columns, Number(piece.columns) || 0);
            }
            const fullySaved =
              alreadySaved.has(day) ||
              ((Number(res.data?.skipped_days) || 0) > 0 && (Number(res.data?.fetched_days) || 0) === 0);
            if (!fullySaved) {
              fetchTimeMs += Date.now() - requestStarted;
              fetchedDone += 1;
            }
            done += 1;
            publishProgress(day, retailer, retailerIndex + 1);
          }
        }
        const stopped = downloadStop.current;
        const label = tables?.length ? tables.join(', ') : 'datasets';
        const names = retailers
          .map((table) => retailerOptions.find((opt) => opt.table === table)?.label || table)
          .join(', ');
        setRefreshMsg(
          stopped
            ? `Stopped after ${done} of ${total} retailer-days (${label}). Saved days are kept.`
            : `Downloaded ${retailers.length} retailer${retailers.length === 1 ? '' : 's'} · ${dates.length} day(s) (${label}): ${names}.`
        );
        await loadCatalog(retailers[0]);
        if (retailers.includes(retailerTable)) {
          await loadMeta();
          await loadWeeks();
          await loadWeekDays();
          await loadWeekFilters();
        }
      } catch (e) {
        const cancelled = e.code === 'ERR_CANCELED' || e.name === 'CanceledError' || downloadStop.current;
        failedRetailer = retailers[Math.min(retailers.length - 1, Math.floor(done / Math.max(dates.length, 1)))] || '';
        if (cancelled) {
          setRefreshMsg(`Stopped after ${done} of ${total} retailer-days. Saved days are kept.`);
          await loadCatalog(retailers[0]);
        } else {
          const who = retailerOptions.find((opt) => opt.table === failedRetailer)?.label || failedRetailer;
          setError(
            e.response?.data?.detail ||
              e.message ||
              `Failed while downloading ${who || 'a retailer'} on ${dates[done % dates.length] || 'a day'}`
          );
        }
      } finally {
        downloadAbort.current = null;
        setRefreshing(false);
        setDownloadProgress((prev) => (prev ? { ...prev, stopped: downloadStop.current } : prev));
      }
    },
    [
      downloadPeriod,
      downloadWeek,
      downloadDay,
      downloadMonth,
      downloadStart,
      downloadEnd,
      downloadWeeks,
      downloadRetailers,
      retailerOptions,
      retailerTable,
      loadCatalog,
      loadMeta,
      loadWeeks,
      loadWeekDays,
      loadWeekFilters,
    ]
  );

  const loadSingleDay = useCallback(
    async (dateStr, { alreadyCached = false, emptyFile = false, tables = ['dashboard'] } = {}) => {
      if (!dateStr || !retailerTable) return;
      setLoadingDay(dateStr);
      setRefreshMsg(null);
      setError(null);
      try {
        if (!alreadyCached) {
          const res = await axios.post(
            `${apiBase}/refresh-data`,
            { date: dateStr, retailer_table: retailerTable, force: !!emptyFile, tables },
            { withCredentials: true, headers: { 'content-type': 'application/json' } }
          );
          setRefreshMsg(res.data?.message || `Loaded ${dateStr}`);
          await loadMeta();
          await loadWeekDays();
        } else {
          setRefreshMsg(`Using cached ${dateStr}`);
        }
        await loadWeekFilters();
        await loadPreview();
      } catch (e) {
        setError(e.response?.data?.detail || e.message || `Failed to load ${dateStr}`);
      } finally {
        setLoadingDay(null);
      }
    },
    [retailerTable, loadMeta, loadWeekDays, loadWeekFilters, loadPreview]
  );

  const exportWeeklyPdf = useCallback(async () => {
    const range = resolvePeriodRange(reportPeriod, {
      week: selectedWeek,
      day: reportDay,
      month: reportMonth,
      start: reportStart,
      end: reportEnd,
      weeks,
    });
    if (!retailerTable || !range) return;
    setExportingPdf(true);
    setError(null);
    try {
      const params = { retailer_table: retailerTable };
      if (reportPeriod === 'weekly' && selectedWeek) params.week = selectedWeek;
      else {
        params.start_date = range.start;
        params.end_date = range.end;
        params.period = reportPeriod;
        params.week = `${range.start}..${range.end}`;
      }
      if (brand) params.brand = brand;
      if (category) params.category = category;
      const res = await axios.get(`${apiBase}/weekly-export.pdf`, {
        params,
        withCredentials: true,
        responseType: 'blob',
      });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      const disposition = res.headers?.['content-disposition'] || '';
      const match = disposition.match(/filename="?([^"]+)"?/);
      link.href = url;
      link.download =
        match?.[1] ||
        shelfPdfFilename([retailerTable, params.week || selectedWeek, category, brand]);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      let message = e.message || 'Failed to export weekly PDF';
      const data = e.response?.data;
      if (data instanceof Blob) {
        try {
          const text = await data.text();
          message = JSON.parse(text).detail || text || message;
        } catch {
          message = 'Failed to export weekly PDF';
        }
      } else if (e.response?.data?.detail) {
        message = e.response.data.detail;
      }
      setError(typeof message === 'string' ? message : 'Failed to export weekly PDF');
    } finally {
      setExportingPdf(false);
    }
  }, [selectedWeek, retailerTable, brand, category, reportPeriod, reportDay, reportMonth, reportStart, reportEnd, weeks]);

  useEffect(() => {
    loadCatalog();
  }, [loadCatalog]);

  useEffect(() => {
    const timer = setTimeout(() => setRawQueryDebounced(rawQuery.trim()), 300);
    return () => clearTimeout(timer);
  }, [rawQuery]);

  const clearRawFilters = () => {
    setRawQuery('');
    setRawQueryDebounced('');
    setRawBrand('');
    setRawCategory('');
    setRawRetailer('');
    setRawDay('');
  };

  useEffect(() => {
    loadMeta();
  }, [loadMeta]);

  useEffect(() => {
    const timer = setTimeout(() => loadWeeks(), 300);
    return () => clearTimeout(timer);
  }, [loadWeeks]);

  useEffect(() => {
    if (!retailerTable || !reportRange) {
      setWeekDays([]);
      setPreview(null);
      setCategories([]);
      setBrands([]);
      setBrandsByCategory({});
      setCategory('');
      setBrand('');
      setHealth(null);
      setSkuRankings(null);
      return;
    }
    setHealth(null);
    setSkuRankings(null);
    const timer = setTimeout(() => {
      loadWeekDays();
      loadWeekFilters();
    }, 100);
    return () => clearTimeout(timer);
  }, [loadWeekDays, loadWeekFilters, reportRange, retailerTable]);

  useEffect(() => {
    if (detailTab !== 'raw' || !reportRange || !retailerTable) return undefined;
    const timer = setTimeout(() => loadPreview(), 150);
    return () => clearTimeout(timer);
  }, [detailTab, loadPreview, reportRange, retailerTable]);

  useEffect(() => {
    if (!downloadRetailers.length) {
      setDownloadWeeks([]);
      setDownloadWeek('');
      return undefined;
    }
    const weekRetailer = downloadRetailers[0];
    let cancelled = false;
    setDownloadWeeksLoading(true);
    axios
      .get(`${apiBase}/snapshots`, {
        params: { retailer_table: weekRetailer },
        withCredentials: true,
      })
      .then((res) => {
        if (cancelled) return;
        const next = res.data?.weeks || [];
        setDownloadWeeks(next);
        setDownloadWeek((prev) =>
          prev && next.some((w) => w.week_key === prev) ? prev : next[0]?.week_key || ''
        );
      })
      .catch(() => {
        if (cancelled) return;
        setDownloadWeeks([]);
        setDownloadWeek('');
      })
      .finally(() => {
        if (!cancelled) setDownloadWeeksLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [downloadRetailers]);

  const downloadRange = useMemo(
    () =>
      resolvePeriodRange(downloadPeriod, {
        week: downloadWeek,
        day: downloadDay,
        month: downloadMonth,
        start: downloadStart,
        end: downloadEnd,
        weeks: downloadWeeks,
      }),
    [downloadPeriod, downloadWeek, downloadDay, downloadMonth, downloadStart, downloadEnd, downloadWeeks]
  );
  const downloadDates = downloadRange ? eachDate(downloadRange.start, downloadRange.end) : [];
  const selectedMetas = retailerOptions.filter((opt) => downloadRetailers.includes(opt.table));
  const visibilityAvailable = selectedMetas.length === 0 || selectedMetas.some((opt) => opt.has_positions !== false);
  const selectedDownloadTables = RAW_DATASETS.map((item) => item.id).filter(
    (id) => downloadTables[id] && (id !== 'promos' || visibilityAvailable)
  );

  const preparedOptions = useMemo(
    () => retailerOptions.filter((opt) => opt.prepared),
    [retailerOptions]
  );
  const downloadOptions = useMemo(
    () =>
      retailerOptions.filter(
        (opt) => !downloadCountry || (opt.country_code || '') === downloadCountry
      ),
    [retailerOptions, downloadCountry]
  );

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Prepare datasets from any ecommerce_prod retailer, filtered by the table suffix
        (<code>_uk</code>, <code>_cz</code>, …). The working view below only lists retailers that
        already have a local CSV. Visibility uses <code>{positionsTable}</code> when it exists.
      </Typography>

      {catalogError && (
        <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setCatalogError(null)}>
          {typeof catalogError === 'string' ? catalogError : JSON.stringify(catalogError)}
          {' '}You can still type a table name.
        </Alert>
      )}

      {meta && meta.config_present === false && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          ecommerce_prod MySQL config missing on server ({meta.config_path}).
        </Alert>
      )}

      {meta?.daily_cache && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Retailer CSV cache: {meta.daily_cache.cached_day_count ?? 0} day(s)
          {meta.daily_cache.latest_date ? ` · latest ${meta.daily_cache.latest_date}` : ''}
          {meta.daily_cache.dir ? (
            <>
              {' '}
              under <code>{meta.daily_cache.dir}</code>
            </>
          ) : null}
        </Alert>
      )}

      {refreshMsg && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setRefreshMsg(null)}>
          {refreshMsg}
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {typeof error === 'string' ? error : JSON.stringify(error)}
        </Alert>
      )}

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 0.5, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Download fontSize="small" />
            Prepare datasets
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Pick a country suffix, then up to {MAX_DOWNLOAD_RETAILERS} retailers. They download one after another
            for the same date range, and show up in the working filter below.
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Country</InputLabel>
              <Select
                label="Country"
                value={downloadCountry}
                onChange={(e) => {
                  const code = e.target.value;
                  setDownloadCountry(code);
                  setDownloadRetailers((prev) => {
                    if (!code) return prev;
                    return prev.filter((table) => {
                      const current = retailerOptions.find((opt) => opt.table === table);
                      return current && current.country_code === code;
                    });
                  });
                }}
              >
                <MenuItem value="">All countries</MenuItem>
                {catalogCountries.map((country) => (
                  <MenuItem key={country.code} value={country.code}>
                    {country.code.toUpperCase()}
                    {country.name && country.name !== 'Unknown' ? ` · ${country.name}` : ''} ({country.count})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Autocomplete
              multiple
              limitTags={3}
              disableCloseOnSelect
              options={downloadOptions}
              value={downloadOptions.filter((opt) => downloadRetailers.includes(opt.table))}
              onChange={(_, values) => {
                const next = values.slice(0, MAX_DOWNLOAD_RETAILERS);
                setDownloadRetailers(next.map((opt) => opt.table));
                const visibility = next.some((opt) => opt.has_positions !== false);
                setDownloadTables((prev) => ({ ...prev, promos: visibility }));
              }}
              getOptionDisabled={(opt) =>
                downloadRetailers.length >= MAX_DOWNLOAD_RETAILERS && !downloadRetailers.includes(opt.table)
              }
              getOptionLabel={(opt) => (opt?.label ? `${opt.label} · ${opt.table}` : opt?.table || '')}
              isOptionEqualToValue={(a, b) => a.table === b.table}
              sx={{ minWidth: 360, flex: '1 1 360px' }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  label={`Retailers to download (${downloadRetailers.length}/${MAX_DOWNLOAD_RETAILERS})`}
                  placeholder={downloadRetailers.length ? '' : 'rohlikall_cz'}
                />
              )}
              renderOption={(props, opt) => (
                <li {...props} key={opt.table}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                    <span>
                      {opt.label} · {opt.table}
                    </span>
                    {opt.prepared ? (
                      <Chip size="small" color="success" label="CSV" sx={{ height: 22 }} />
                    ) : null}
                  </Box>
                </li>
              )}
            />
            <PeriodFields
              period={downloadPeriod}
              onPeriod={setDownloadPeriod}
              day={downloadDay}
              onDay={setDownloadDay}
              month={downloadMonth}
              onMonth={setDownloadMonth}
              start={downloadStart}
              onStart={setDownloadStart}
              end={downloadEnd}
              onEnd={setDownloadEnd}
              weekControl={
                <FormControl size="small" sx={{ minWidth: 360 }}>
                  <InputLabel>Week to download</InputLabel>
                  <Select
                    label="Week to download"
                    value={downloadWeeks.some((w) => w.week_key === downloadWeek) ? downloadWeek : ''}
                    onChange={(e) => setDownloadWeek(e.target.value)}
                    disabled={!downloadRetailers.length || downloadWeeksLoading || downloadWeeks.length === 0}
                    displayEmpty
                  >
                    {downloadWeeks.length === 0 && (
                      <MenuItem value="" disabled>
                        {downloadWeeksLoading ? 'Loading weeks…' : 'Pick a retailer'}
                      </MenuItem>
                    )}
                    {downloadWeeks.map((w) => (
                      <MenuItem key={w.week_key} value={w.week_key}>
                        {w.week_label}
                        {w.scrape_days ? ` · ${w.scrape_days} scrape day${w.scrape_days === 1 ? '' : 's'}` : ''}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              }
            />
            {RAW_DATASETS.map((item) => {
              const unavailable = item.id === 'promos' && !visibilityAvailable;
              return (
                <FormControlLabel
                  key={item.id}
                  control={
                    <Checkbox
                      size="small"
                      checked={!unavailable && !!downloadTables[item.id]}
                      disabled={refreshing || unavailable}
                      onChange={(e) =>
                        setDownloadTables((prev) => ({ ...prev, [item.id]: e.target.checked }))
                      }
                    />
                  }
                  label={unavailable ? `${item.label} (no positions table)` : item.label}
                />
              );
            })}
            <Button
              variant="contained"
              color={refreshing ? 'warning' : 'primary'}
              startIcon={refreshing ? undefined : <Download />}
              onClick={() => (refreshing ? stopDownload() : refreshDailyCsv(selectedDownloadTables))}
              disabled={
                !refreshing &&
                (!downloadRetailers.length ||
                  !selectedDownloadTables.length ||
                  !downloadDates.length ||
                  downloadDates.length > MAX_DOWNLOAD_DAYS)
              }
            >
              {refreshing ? 'Stop' : 'Download'}
            </Button>
            {downloadEtaLabel && (
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                ETA {downloadEtaLabel}
              </Typography>
            )}
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            {downloadRange
              ? `${downloadDates.length} day${downloadDates.length === 1 ? '' : 's'} · ${downloadRange.label}`
              : 'Choose a weekly, daily, monthly, or custom range.'}
            {downloadRetailers.length > 1
              ? ` · ${downloadRetailers.length} retailers, downloaded one after another.`
              : ''}
            {downloadDates.length > MAX_DOWNLOAD_DAYS ? ` Narrow the range to ${MAX_DOWNLOAD_DAYS} days or fewer.` : ''}
            {' '}Prices, availability, and visibility are included when ticked. Stop keeps every day already saved.
          </Typography>
          {downloadProgress && (
            <Box sx={{ mt: 1.5, maxWidth: 640 }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {downloadProgress.retailerLabel ? `${downloadProgress.retailerLabel} · ` : ''}
                {downloadProgress.retailerTotal > 1
                  ? `${downloadProgress.retailerIndex}/${downloadProgress.retailerTotal} · `
                  : ''}
                {downloadProgress.done}/{downloadProgress.total}
                {' · '}
                {downloadPortion(downloadProgress, etaNow, refreshing).percent}%
                {refreshing && downloadProgress.day ? ` · ${downloadProgress.day}` : ''}
                {downloadProgress.stopped ? ' · stopped' : ''}
              </Typography>
              {downloadProgress.done ? (
                RAW_DATASETS.filter((item) => downloadProgress.tables?.includes(item.id)).map((item) => {
                  const shape = downloadProgress.shapes?.[item.id] || { rows: 0, columns: 0 };
                  const avgRows = Math.round((shape.rows || 0) / downloadProgress.done);
                  return (
                    <Typography key={item.id} variant="body2" color="text.secondary">
                      {item.label}: {formatCount(shape.rows)} × {formatCount(shape.columns)} · {formatCount(avgRows)} rows/day
                    </Typography>
                  );
                })
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Waiting for the first day
                </Typography>
              )}
              <LinearProgress
                variant="determinate"
                value={downloadPortion(downloadProgress, etaNow, refreshing).bar}
                sx={{ mt: 0.5 }}
              />
            </Box>
          )}
        </CardContent>
      </Card>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', mb: weekDays.length ? 2 : 0 }}>
            <Autocomplete
              options={preparedOptions}
              value={preparedOptions.find((opt) => opt.table === retailerTable) || null}
              onChange={(_, value) => setRetailerTable(value?.table || '')}
              getOptionLabel={(opt) => (opt?.label ? `${opt.label} · ${opt.table}` : opt?.table || '')}
              isOptionEqualToValue={(a, b) => a.table === b.table}
              filterOptions={(options, state) => {
                const current = options.find((opt) => opt.table === retailerTable);
                const currentLabel = current
                  ? current.label
                    ? `${current.label} · ${current.table}`
                    : current.table
                  : '';
                const typed = (state.inputValue || '').trim();
                if (!typed || typed === currentLabel) return options;
                const query = typed.toLowerCase();
                return options.filter((opt) =>
                  `${opt.label || ''} ${opt.table || ''}`.toLowerCase().includes(query)
                );
              }}
              sx={{ minWidth: 320 }}
              noOptionsText="No downloaded retailers yet"
              renderInput={(params) => (
                <TextField {...params} size="small" label="Working retailer" placeholder="Downloaded retailers" />
              )}
            />
            <PeriodFields
              period={reportPeriod}
              onPeriod={setReportPeriod}
              day={reportDay}
              onDay={setReportDay}
              month={reportMonth}
              onMonth={setReportMonth}
              start={reportStart}
              onStart={setReportStart}
              end={reportEnd}
              onEnd={setReportEnd}
              weekControl={
            <FormControl size="small" sx={{ minWidth: 420 }}>
              <InputLabel>Week</InputLabel>
              <Select
                label="Week"
                value={weeks.some((w) => w.week_key === selectedWeek) ? selectedWeek : ''}
                onChange={(e) => setSelectedWeek(e.target.value)}
                disabled={weeksLoading || weeks.length === 0}
                displayEmpty
              >
                {weeks.length === 0 && (
                  <MenuItem value="" disabled>
                    {weeksLoading ? 'Loading weeks…' : 'No weeks found'}
                  </MenuItem>
                )}
                {weeks.map((w) => {
                  const csvLabel = weekCsvLabel(w);
                  return (
                    <MenuItem key={w.week_key} value={w.week_key}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                        <span>
                          {w.week_label}
                          {w.scrape_days
                            ? ` · ${w.scrape_days} scrape day${w.scrape_days === 1 ? '' : 's'}`
                            : ''}
                        </span>
                        {csvLabel ? (
                          <Chip size="small" color="success" label={csvLabel} sx={{ height: 22 }} />
                        ) : w.in_dataset ? (
                          <Chip size="small" variant="outlined" label="Not on disk" sx={{ height: 22 }} />
                        ) : null}
                      </Box>
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
              }
            />
            <Button
              startIcon={<Refresh />}
              onClick={() => {
                loadWeeks(true);
                loadWeekFilters();
              }}
              disabled={weeksLoading}
            >
              Reload weeks
            </Button>
            {weeksLoading && <CircularProgress size={20} />}
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: weekDays.length ? 1 : 0 }}>
            <Storefront sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'text-bottom' }} />
            {retailerLabel}
            {selectedMeta?.country ? ` · ${selectedMeta.country}` : ''}
            {' · prices/availability from '}
            <code>{retailerTable || '…'}</code>
            {' · visibility from '}
            <code>{positionsTable}</code>
          </Typography>

          {reportRange && weekDays.length > 0 && (
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                Days in {reportRange.label} — green = prices on disk; P/A/V badges = prices / availability /
                visibility
              </Typography>
              <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                {weekDays.map((d) => {
                  const busy = loadingDay === d.date;
                  const chipColor = d.cached ? 'success' : d.empty_file ? 'warning' : 'default';
                  const chipVariant = d.cached || d.empty_file ? 'filled' : 'outlined';
                  const notInDataset = d.has_scrape === false;
                  const badges = [
                    d.has_dashboard || d.cached ? 'P' : '',
                    d.has_product_statuses ? 'A' : '',
                    d.has_promos ? 'V' : '',
                  ]
                    .filter(Boolean)
                    .join('');
                  return (
                    <Chip
                      key={d.date}
                      clickable={!busy && !refreshing}
                      disabled={busy || refreshing}
                      color={chipColor}
                      variant={chipVariant}
                      icon={busy ? <CircularProgress size={14} color="inherit" /> : undefined}
                      label={`${d.weekday} ${d.date.slice(5)}${badges ? ` · ${badges}` : ''}${!notInDataset && d.has_scrape && !d.cached && !d.empty_file ? ' · in data' : ''}`}
                      sx={notInDataset ? { opacity: 0.45 } : undefined}
                      title={
                        d.has_scrape === true && !d.cached
                          ? 'In the dataset, not downloaded yet'
                          : d.cached
                            ? 'Local CSV on disk'
                            : notInDataset
                              ? 'No scrape this day'
                              : ''
                      }
                      onClick={() => {
                        if (!busy && !refreshing) {
                          loadSingleDay(d.date, {
                            alreadyCached: !!d.cached,
                            emptyFile: !!d.empty_file,
                            tables: ['dashboard'],
                          });
                        }
                      }}
                    />
                  );
                })}
              </Stack>
            </Box>
          )}
        </CardContent>
      </Card>

      <Card sx={{ mb: 3 }}>
        <Tabs
          value={detailTab}
          onChange={(_, value) => setDetailTab(value)}
          sx={{ px: 2, borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab value="health" label="Brand health" />
          <Tab value="raw" label="Raw data" />
        </Tabs>
        <CardContent>
          {detailTab === 'health' && (
            <Box>
              <Autocomplete
                options={categories}
                value={categories.includes(category) ? category : null}
                onChange={(_, value) => setCategory(value || '')}
                loading={filtersLoading}
                disabled={filtersLoading || categories.length === 0}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Category"
                    size="small"
                    placeholder={filtersLoading ? 'Loading…' : 'Select category'}
                  />
                )}
                sx={{ mb: 1.5 }}
              />
              <Autocomplete
                options={brands}
                value={brands.includes(brand) ? brand : null}
                onChange={(_, value) => setBrand(value || '')}
                loading={filtersLoading}
                disabled={filtersLoading || brands.length === 0}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Brand"
                    size="small"
                    placeholder={filtersLoading ? 'Loading…' : 'Select brand'}
                  />
                )}
                sx={{ mb: 2 }}
              />
              <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                <Button
                  variant="contained"
                  disabled={healthLoading || !reportRange || !brand || !category || !retailerTable}
                  onClick={loadHealth}
                >
                  {healthLoading ? 'Loading…' : 'Run weekly intelligence'}
                </Button>
                <Button
                  variant="outlined"
                  startIcon={exportingPdf ? <CircularProgress size={16} /> : <PictureAsPdf />}
                  disabled={exportingPdf || !reportRange || !retailerTable}
                  onClick={exportWeeklyPdf}
                >
                  {exportingPdf ? 'Preparing PDF…' : 'Customer report PDF'}
                </Button>
                <PublishReportControls
                  disabled={!reportRange || !retailerTable}
                  payload={
                    reportPeriod === 'weekly'
                      ? {
                          retailer_table: retailerTable,
                          week: selectedWeek,
                          brand: brand || null,
                          category: category || null,
                          period: 'weekly',
                        }
                      : {
                          retailer_table: retailerTable,
                          week: reportRange ? `${reportRange.start}..${reportRange.end}` : null,
                          start_date: reportRange?.start,
                          end_date: reportRange?.end,
                          period: reportPeriod,
                          brand: brand || null,
                          category: category || null,
                        }
                  }
                />
              </Stack>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                PDF and the published page compare this period with the previous one for {retailerLabel} only.
                {brand && category ? ` Scoped to ${brand} · ${category}.` : ' Leave brand and category empty for the whole catalog.'}
              </Typography>
              {filtersLoading && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                  Inferring brands/categories from weekly dataset…
                </Typography>
              )}
              {health && (
                <Paper variant="outlined" sx={{ mt: 2, p: 2 }}>
                  <Typography variant="overline" color="text.secondary">
                    {health.retailer_label || retailerLabel} · {health.country} · {health.category}
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 700 }}>
                    {health.brand}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Digital shelf health (from {health.sku_count ?? '—'} SKUs)
                  </Typography>
                  {health.datasets && (
                    <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap" sx={{ mb: 1 }}>
                      {[
                        ['dashboard', 'Prices'],
                        ['product_statuses', 'Availability'],
                        ['promos', 'Visibility'],
                      ].map(([key, label]) => (
                        <Chip
                          key={key}
                          size="small"
                          label={label}
                          color={health.datasets[key] ? 'success' : 'default'}
                          variant={health.datasets[key] ? 'filled' : 'outlined'}
                        />
                      ))}
                    </Stack>
                  )}
                  {health.active_dimensions?.length > 0 && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                      Scoring: {health.active_dimensions.join(', ')}
                    </Typography>
                  )}
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>
                    {health.health_score ?? health.score}/100
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 0.5 }}>
                    Rank #{health.rank} / {health.total_brands}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ color: health.wow_change < 0 ? 'error.main' : 'success.main', mb: 1.5 }}
                  >
                    {formatWow(health.wow_change)}
                  </Typography>
                  {health.skus_with_issues > 0 && (
                    <Alert severity="info" sx={{ mb: 1.5, py: 0.5 }}>
                      {health.skus_with_issues} SKU{health.skus_with_issues !== 1 ? 's' : ''} with significant
                      shelf issues
                    </Alert>
                  )}
                  {health.top_issues?.length > 0 && (
                    <Box sx={{ mb: 1.5 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        Why the brand score (aggregated)
                      </Typography>
                      <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                        {health.top_issues.map((issue) => (
                          <Typography component="li" variant="body2" key={issue}>
                            {issue}
                          </Typography>
                        ))}
                      </Box>
                    </Box>
                  )}
                  <Typography variant="body2" color="text.secondary">
                    vs {health.competitor} ({health.competitor_score}) ·{' '}
                    {reportRange?.label || health.week || 'period'} · scrape {health.snapshot_date}
                  </Typography>
                </Paper>
              )}
              {skuRankings?.skus?.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    SKU health (child of brand · mean = {skuRankings.brand_health_score ?? '—'}/100)
                    {skuLoading ? ' …' : ''}
                  </Typography>
                  <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 360 }}>
                    <Table stickyHeader size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>SKU</TableCell>
                          <TableCell align="right">Health</TableCell>
                          <TableCell>Main issue</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {skuRankings.skus.map((sku, i) => (
                          <TableRow
                            key={sku.sku_key || i}
                            hover
                            sx={{ cursor: 'pointer' }}
                            onClick={() => openSkuDetail(sku)}
                          >
                            <TableCell sx={{ maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {sku.title}
                            </TableCell>
                            <TableCell align="right">{sku.health_score}</TableCell>
                            <TableCell>{sku.main_issue_display ?? sku.main_issue ?? '—'}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}
            </Box>
          )}
          {detailTab === 'raw' && (
            <Box>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
              <FormControl size="small" sx={{ minWidth: 180 }}>
                <InputLabel>Dataset</InputLabel>
                <Select label="Dataset" value={table} onChange={(e) => setTable(e.target.value)}>
                  {RAW_DATASETS.map((item) => (
                    <MenuItem key={item.id} value={item.id}>
                      {item.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button startIcon={<Refresh />} onClick={loadPreview} disabled={loading || !reportRange}>
                Refresh
              </Button>
            {preview && (
              <Typography variant="body2" color="text.secondary">
                {preview.estimated_total_rows != null
                  ? `${preview.estimated_total_rows.toLocaleString()} rows in week`
                  : `${preview.returned} rows`}
              </Typography>
            )}
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            Normalized rows already downloaded for {retailerLabel}. Filters apply before paging. Visibility comes from {positionsTable} when that table was downloaded.
          </Typography>
            <RawDataFilterBar
              query={rawQuery}
              onQueryChange={setRawQuery}
              brand={rawBrand}
              category={rawCategory}
              retailer={rawRetailer}
              day={rawDay}
              onBrandChange={setRawBrand}
              onCategoryChange={setRawCategory}
              onRetailerChange={setRawRetailer}
              onDayChange={setRawDay}
              facets={preview?.facets}
              onClear={clearRawFilters}
            />

          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          )}

          {!loading && preview && (preview.estimated_total_rows > 0 || preview.rows?.length > 0) && (
            <TablePagination
              component="div"
              count={preview.estimated_total_rows ?? preview.rows.length}
              page={rawPage}
              onPageChange={(_, nextPage) => setRawPage(nextPage)}
              rowsPerPage={rawPageSize}
              onRowsPerPageChange={(event) => {
                setRawPageSize(Number(event.target.value));
                setRawPage(0);
              }}
              rowsPerPageOptions={RAW_PAGE_SIZES}
            />
          )}

          {!loading && preview && preview.rows?.length > 0 && (
            <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 560 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    {(preview.columns || []).map((col) => (
                      <TableCell key={col} sx={{ whiteSpace: 'nowrap' }}>
                        {col}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {preview.rows.map((row, i) => (
                    <TableRow key={i}>
                      {(preview.columns || []).map((col) => (
                        <TableCell key={col} sx={{ whiteSpace: 'nowrap' }}>
                          {formatCell(row[col])}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {!loading && preview && !(preview.estimated_total_rows > 0) && !preview.rows?.length && (
            <Typography color="text.secondary" sx={{ mt: 2 }}>
              No rows in this dataset for the selected week. Download prices, availability, or visibility first.
            </Typography>
          )}
            </Box>
          )}
        </CardContent>
      </Card>

      <SnusProductDetailPopup
        open={skuPopupOpen}
        onClose={() => setSkuPopupOpen(false)}
        detail={skuDetail}
        loading={skuDetailLoading}
        error={skuDetailError}
        skuKey={selectedSkuKey}
        country={health?.country || selectedMeta?.country || retailerTable}
        brand={brand}
        category={category}
        week={selectedWeek}
        retailerTable={retailerTable}
      />
    </Box>
  );
}
